import { apiClient, apiDownload } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import type {
  AdminCancelOrderBody,
  AdminOrderRequestsListResponse,
  CancellationRequestBody,
  CheckoutPayload,
  OrdersListResponse,
  UpdateOrderRequestBody,
} from '../../types/api';
import type { AdminOrderRequest, Order, OrderRequest, OrderRequestStatus } from '../../types';
import { triggerFileDownload } from '../../utils/downloadBlob';
import { buildCreateOrderRequest } from '../../utils/orderRequest';

export async function httpFetchOrders(): Promise<Order[]> {
  const response = await apiClient<OrdersListResponse | Order[]>(endpoints.orders.list);
  return Array.isArray(response) ? response : response.orders;
}

export async function httpCreateOrder(payload: CheckoutPayload): Promise<Order> {
  return apiClient<Order>(endpoints.checkout.create, {
    method: 'POST',
    body: buildCreateOrderRequest(payload),
  });
}

export async function httpGetOrder(orderId: string): Promise<Order> {
  return apiClient<Order>(endpoints.orders.detail(orderId));
}

export async function httpFetchAllOrders(): Promise<Order[]> {
  const response = await apiClient<OrdersListResponse | Order[]>(endpoints.admin.orders.list);
  return Array.isArray(response) ? response : response.orders;
}

export async function httpUpdateOrderStatus(orderId: string, status: Order['status']): Promise<Order> {
  return apiClient<Order>(endpoints.admin.orders.updateStatus(orderId), {
    method: 'PATCH',
    body: { status },
  });
}

export async function httpUpdateOrderTracking(
  orderId: string,
  trackingNumber: string | null,
): Promise<Order> {
  return apiClient<Order>(endpoints.admin.orders.updateTracking(orderId), {
    method: 'PATCH',
    body: { trackingNumber },
  });
}

export async function httpUploadAdminOrderInvoice(orderId: string, file: File): Promise<Order> {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient<Order>(endpoints.admin.orders.uploadInvoice(orderId), {
    method: 'POST',
    body: formData,
  });
}

export async function httpDownloadOrderInvoice(orderId: string): Promise<void> {
  const file = await apiDownload(endpoints.orders.invoice(orderId));
  triggerFileDownload(file);
}

export async function httpDownloadAdminOrderInvoice(orderId: string): Promise<void> {
  const file = await apiDownload(endpoints.admin.orders.downloadInvoice(orderId));
  triggerFileDownload(file);
}

export async function httpSubmitCancellationRequest(
  orderId: string,
  reason: string,
): Promise<OrderRequest> {
  const body: CancellationRequestBody = { reason };
  return apiClient<OrderRequest>(endpoints.orders.cancellationRequest(orderId), {
    method: 'POST',
    body,
  });
}

export async function httpWithdrawOrderRequest(orderId: string, requestId: string): Promise<void> {
  await apiClient<void>(endpoints.orders.withdrawRequest(orderId, requestId), {
    method: 'DELETE',
  });
}

export async function httpAdminCancelOrder(
  orderId: string,
  adminNote?: string | null,
): Promise<Order> {
  const body: AdminCancelOrderBody = { adminNote: adminNote ?? null };
  return apiClient<Order>(endpoints.admin.orders.cancel(orderId), {
    method: 'POST',
    body,
  });
}

export async function httpFetchAdminOrderRequests(
  status?: OrderRequestStatus,
): Promise<AdminOrderRequest[]> {
  const query = status ? `?status=${status}` : '';
  const response = await apiClient<AdminOrderRequestsListResponse | AdminOrderRequest[]>(
    `${endpoints.admin.orderRequests.list}${query}`,
  );
  return Array.isArray(response) ? response : response.requests;
}

export async function httpUpdateAdminOrderRequest(
  requestId: string,
  status: 'approved' | 'rejected',
  adminNote?: string | null,
): Promise<AdminOrderRequest> {
  const body: UpdateOrderRequestBody = { status, adminNote: adminNote ?? null };
  return apiClient<AdminOrderRequest>(endpoints.admin.orderRequests.update(requestId), {
    method: 'PATCH',
    body,
  });
}

export { buildCreateOrderRequest };
